import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Employee, EmployeeStatus, MedicalStatus } from "../types/processTypes";
import { isNoEmbassyCountry } from "../processConstants";
import {
  Accordion,
  AccordionDetails,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { MedicalStatusChip } from "./MedicalStatusChip";
import { alpha } from "@mui/material/styles";
import { stepConnectorClasses } from "@mui/material/StepConnector";
import { Form, Formik, FormikProps } from "formik";

import { Check, ExpandMore, Save } from "@mui/icons-material";
import {
  changeEmployeeStatus,
  selectProcesses,
  setSelectedEmployee,
} from "../processSlices";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectPreference } from "../../preferences/preferencesSlice";
import { DateTimePicker } from "@mui/x-date-pickers";
import { selectAuth } from "../../auth/authSlice";
import Toast from "../../../components/Layout/Toast";
import { isPrivilegedTransaction } from "../../../utils/authUtils";
import { Role } from "../../auth/types/authType";
import { FormikTextField } from "../../../components/Layout/FormikTextField";
import { StyledAccordionSummary } from "../../../styles/componentStyled";

/** Post-departure disposition + “working well” (`departed`). */
const DISPOSITION_STATUS_OPTIONS: EmployeeStatus[] = [
  EmployeeStatus.departed,
  EmployeeStatus.lost,
  EmployeeStatus.returned,
  EmployeeStatus.has_complain,
  EmployeeStatus.contract_completed,
  EmployeeStatus.other,
];

const PIPELINE_ORDER: EmployeeStatus[] = [
  EmployeeStatus.pending,
  EmployeeStatus.new,
  EmployeeStatus.selected,
  EmployeeStatus.contracted,
  EmployeeStatus.visa_issued,
  EmployeeStatus.labour_issued,
  EmployeeStatus.flight_booked,
  EmployeeStatus.departed,
];

/**
 * Show "Discontinue process" switch from `selected` through the pre-departure
 * pipeline (or when already discontinued). Pending/new candidates use the
 * Stop action button on the list instead.
 */
const SHOW_DISCONTINUED_PROCESS_STATUSES = new Set<EmployeeStatus>([
  EmployeeStatus.selected,
  EmployeeStatus.contracted,
  EmployeeStatus.visa_issued,
  EmployeeStatus.labour_issued,
  EmployeeStatus.flight_booked,
  EmployeeStatus.discontinued,
]);

function pipelineRank(status: EmployeeStatus): number {
  const i = PIPELINE_ORDER.indexOf(status);
  if (i >= 0) return i;
  if (DISPOSITION_STATUS_OPTIONS.includes(status)) {
    return PIPELINE_ORDER.indexOf(EmployeeStatus.departed) + 1;
  }
  return 0;
}

function showAfterDeparted(args: {
  status: EmployeeStatus;
  ticketBooked: boolean;
  flightDeparted?: boolean;
}): boolean {
  if (DISPOSITION_STATUS_OPTIONS.includes(args.status)) {
    return true;
  }
  return (
    args.ticketBooked &&
    (args.status === EmployeeStatus.departed || Boolean(args.flightDeparted))
  );
}

/** Submit status when not forcing disposition from the accordion. */
function computePipelineSubmitStatus(
  status: EmployeeStatus,
  v: Pick<Employee, "visaIssued" | "labourIssued" | "ticketBooked" | "flight">,
): EmployeeStatus {
  const departedOn =
    status === EmployeeStatus.departed || Boolean(v.flight?.departed);
  if (departedOn && v.ticketBooked && v.labourIssued && v.visaIssued) {
    return EmployeeStatus.departed;
  }
  if (v.ticketBooked && v.labourIssued && v.visaIssued) {
    return EmployeeStatus.flight_booked;
  }
  if (v.labourIssued) {
    return EmployeeStatus.labour_issued;
  }
  if (v.visaIssued) {
    return EmployeeStatus.visa_issued;
  }
  if (status === EmployeeStatus.pending) {
    return EmployeeStatus.pending;
  }
  if (status === EmployeeStatus.new) {
    return EmployeeStatus.new;
  }
  if (status === EmployeeStatus.selected) {
    return EmployeeStatus.selected;
  }
  return EmployeeStatus.contracted;
}

function resolveSubmitCurrentStatusDate(
  submitStatus: EmployeeStatus,
  values: Employee,
  original?: Employee,
): Date | undefined {
  const statusChanging =
    original?.status != null && submitStatus !== original.status;

  if (submitStatus === EmployeeStatus.visa_issued) {
    const visaChanged =
      values.visaIssuedDate != null &&
      !datesEqual(values.visaIssuedDate, original?.visaIssuedDate);
    if (visaChanged) return new Date(values.visaIssuedDate!);
    if (statusChanging) return undefined;
  }
  if (submitStatus === EmployeeStatus.labour_issued) {
    const lmisChanged =
      values.lmisIssuedDate != null &&
      !datesEqual(values.lmisIssuedDate, original?.lmisIssuedDate);
    if (lmisChanged) return new Date(values.lmisIssuedDate!);
    if (statusChanging) return undefined;
  }
  if (submitStatus === EmployeeStatus.flight_booked) {
    const flightDateTime = values.flight?.flightDateTime;
    const flightChanged =
      flightDateTime != null &&
      !datesEqual(flightDateTime, original?.flight?.flightDateTime);
    if (flightChanged) return new Date(flightDateTime);
    if (statusChanging) return undefined;
  }

  const dateExplicitlyChanged =
    values.currentStatusDate != null &&
    !datesEqual(values.currentStatusDate, original?.currentStatusDate);

  if (statusChanging && !dateExplicitlyChanged) {
    return undefined;
  }

  if (values.currentStatusDate) {
    return new Date(values.currentStatusDate);
  }
  return undefined;
}

function pipelineDateChanged(
  next?: Date | string | null,
  original?: Date | string | null,
): boolean {
  return next != null && !datesEqual(next, original);
}

function datesEqual(
  a?: Date | string | null,
  b?: Date | string | null,
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return new Date(a).getTime() === new Date(b).getTime();
}

function PipelineStepIcon(props: {
  active?: boolean;
  completed?: boolean;
  className?: string;
}) {
  const { active, completed } = props;
  if (completed) {
    return (
      <Box
        sx={{
          width: 24,
          height: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Check sx={{ color: "success.main", fontSize: "1.25rem" }} />
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        border: "2px solid",
        borderColor: "common.white",
        bgcolor: active ? "rgba(13, 99, 125, 0.44)" : "transparent",
        boxShadow: active ? "0 0 0 2px rgba(255,255,255,0.25)" : "none",
        opacity: 1,
      }}
    />
  );
}

interface CurrentStatusProps {
  currentEmployee?: Employee;
  handleDialogClose: Function;
}

export const ProcessStages = ({
  currentEmployee,
  handleDialogClose,
}: CurrentStatusProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const { locale } = useAppSelector(selectPreference);

  const [status, setStatus] = useState<EmployeeStatus>(EmployeeStatus.new);
  const [changeEmployeeStatusOnly, setChangeEmployeeStatusOnly] =
    useState(false);
  /** Status before toggling “Discontinued process” on (used to restore when toggled off). */
  const preDiscontinuedStatusRef = useRef<EmployeeStatus | null>(null);

  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);

  const {
    selectedEmployee,
    error: changeFailed,
    success: statusChanged,
  } = useAppSelector(selectProcesses);

  const privileged = useCallback(
    (name: string) =>
      isPrivilegedTransaction((user?.roles ?? []) as Role[], name),
    [user?.roles],
  );

  const broadStatus = privileged("ProcessControl.ManageStatuses");
  const canVisa = broadStatus || privileged("ProcessControl.IssueVisa");
  const canLabour = broadStatus || privileged("ProcessControl.IssueLabour");
  const canTicket = broadStatus || privileged("ProcessControl.BookTicket");
  const canDeparted = broadStatus || privileged("ProcessControl.MarkDeparted");
  const canDisposition =
    broadStatus || privileged("ProcessControl.SetDisposition");
  const canDiscontinueProcess = privileged("ProcessControl.Discontinue");
  const canAddMedical = privileged("Records.AddMedical");
  const canAddCoc = privileged("Records.AddCoc");
  const canEditDiscontinuedProcess = broadStatus || canDiscontinueProcess;
  const canEditCocSwitch = broadStatus || canAddCoc;

  const canSave = useMemo(
    () =>
      broadStatus ||
      privileged("ProcessControl.Select") ||
      privileged("ProcessControl.Contract") ||
      privileged("ProcessControl.IssueVisa") ||
      privileged("ProcessControl.IssueLabour") ||
      privileged("ProcessControl.BookTicket") ||
      privileged("ProcessControl.MarkDeparted") ||
      privileged("ProcessControl.SetDisposition"),
    [broadStatus, privileged],
  );

  const canSubmitProcessStages =
    canSave || canDiscontinueProcess || canAddMedical || canAddCoc;

  useEffect(() => {
    if (
      currentEmployee &&
      currentEmployee._id &&
      currentEmployee._id.length > 10
    ) {
      dispatch(setSelectedEmployee(currentEmployee));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentEmployee]);

  useEffect(() => {
    setChangeEmployeeStatusOnly(broadStatus);
  }, [broadStatus]);

  useEffect(() => {
    if (selectedEmployee?.status) {
      setStatus(selectedEmployee.status as EmployeeStatus);
    }
  }, [selectedEmployee]);

  useEffect(() => {
    preDiscontinuedStatusRef.current = null;
  }, [selectedEmployee?._id]);

  const resolveSubmitStatus = (
    values: Employee,
    statusState: EmployeeStatus,
    accordionVisible: boolean,
  ): EmployeeStatus => {
    if (statusState === EmployeeStatus.discontinued) {
      return EmployeeStatus.discontinued;
    }
    if (statusState === EmployeeStatus.stopped) {
      return EmployeeStatus.stopped;
    }
    if (accordionVisible && DISPOSITION_STATUS_OPTIONS.includes(statusState)) {
      return statusState;
    }
    return computePipelineSubmitStatus(statusState, values);
  };

  return (
    <Box
      alignItems="center"
      sx={{
        m: { xs: 0, md: 2 },
        px: { xs: 0, md: 2 },
      }}
    >
      <Formik
        enableReinitialize={true}
        key={
          selectedEmployee?._id
            ? [
                selectedEmployee._id,
                selectedEmployee.updatedAt != null
                  ? String(selectedEmployee.updatedAt)
                  : "",
                String(selectedEmployee.status ?? ""),
              ].join(":")
            : "no-employee"
        }
        initialValues={selectedEmployee as Employee}
        onSubmit={async (values, actions) => {
          actions.setSubmitting(false);
          if (!values?._id) {
            return;
          }
          if (values.visaIssued && !values.visaIssuedDate) {
            actions.setFieldError("visaIssuedDate", t("required") as string);
            return;
          }
          if (values.labourIssued && !values.lmisIssuedDate) {
            actions.setFieldError("lmisIssuedDate", t("required") as string);
            return;
          }
          if (values.ticketBooked && !values.flight?.flightDateTime) {
            actions.setFieldError(
              "flight.flightDateTime",
              t("required") as string,
            );
            return;
          }
          const accordionVisible = showAfterDeparted({
            status,
            ticketBooked: Boolean(values.ticketBooked),
            flightDeparted: Boolean(values.flight?.departed),
          });
          const submitStatus = resolveSubmitStatus(
            values,
            status,
            accordionVisible,
          );
          const requiresFlightDate =
            submitStatus !== EmployeeStatus.discontinued &&
            (submitStatus === EmployeeStatus.departed ||
              submitStatus === EmployeeStatus.flight_booked);
          const departedFlag =
            submitStatus !== EmployeeStatus.discontinued &&
            (submitStatus === EmployeeStatus.departed ||
              Boolean(values.flight?.departed));
          const original = selectedEmployee;
          const newlyVisaIssued =
            Boolean(values.visaIssued) && !original?.visaIssued;
          const newlyLabourIssued =
            Boolean(values.labourIssued) && !original?.labourIssued;
          const newlyTicketBooked =
            Boolean(values.ticketBooked) && !original?.ticketBooked;
          const sendVisaDate =
            values.visaIssued &&
            (newlyVisaIssued ||
              pipelineDateChanged(
                values.visaIssuedDate,
                original?.visaIssuedDate,
              ));
          const sendLmisDate =
            values.labourIssued &&
            (newlyLabourIssued ||
              pipelineDateChanged(
                values.lmisIssuedDate,
                original?.lmisIssuedDate,
              ));
          const sendFlightDate =
            requiresFlightDate &&
            (newlyTicketBooked ||
              pipelineDateChanged(
                values.flight?.flightDateTime,
                original?.flight?.flightDateTime,
              ));
          const result = await dispatch(
            changeEmployeeStatus({
              _id: values._id,
              status: submitStatus,
              currentStatusDate: resolveSubmitCurrentStatusDate(
                submitStatus,
                values,
                original,
              ),
              currentStatusRemark: values.currentStatusRemark,
              cocCompleted: values.cocCompleted,
              biometricsCompleted: values.biometricsCompleted,
              visaIssued: values.visaIssued,
              visaIssuedDate:
                sendVisaDate && values.visaIssuedDate
                  ? new Date(values.visaIssuedDate)
                  : undefined,
              labourIssued: values.labourIssued,
              lmisIssuedDate:
                sendLmisDate && values.lmisIssuedDate
                  ? new Date(values.lmisIssuedDate)
                  : undefined,
              ticketBooked: values.ticketBooked,
              flight: requiresFlightDate
                ? {
                    ...values.flight,
                    flightDateTime:
                      sendFlightDate && values.flight?.flightDateTime
                        ? new Date(values.flight.flightDateTime)
                        : undefined,
                    departed: departedFlag,
                  }
                : {
                    ...values.flight,
                    departed: departedFlag,
                  },
            }),
          );
          if (changeEmployeeStatus.fulfilled.match(result)) {
            handleDialogClose(true);
          }
        }}
      >
        {(props: FormikProps<Employee>) => {
          const v = props.values;
          /** Medical result was Unfit — blocks forward stages. */
          const medicalUnfit = v.medicalStatus === MedicalStatus.unfit;
          /** No-embassy destinations (Kuwait/Qatar/UAE) skip the biometrics step. */
          const isNoEmbassy = isNoEmbassyCountry(
            v.additionalData?.appliedCountry,
          );
          const biometricsOn = Boolean(v.biometricsCompleted);
          const selectedOn =
            status !== EmployeeStatus.new &&
            status !== EmployeeStatus.pending &&
            status !== EmployeeStatus.stopped;
          const contractedOn =
            pipelineRank(status) >=
            PIPELINE_ORDER.indexOf(EmployeeStatus.contracted);
          const visaOn = Boolean(v.visaIssued);
          const labourOn = Boolean(v.labourIssued);
          const ticketOn = Boolean(v.ticketBooked);
          const departedSwitchOn =
            status === EmployeeStatus.departed || Boolean(v.flight?.departed);
          const accordionOpen = showAfterDeparted({
            status,
            ticketBooked: ticketOn,
            flightDeparted: Boolean(v.flight?.departed),
          });

          const dispositionValue: EmployeeStatus =
            DISPOSITION_STATUS_OPTIONS.includes(status)
              ? status
              : EmployeeStatus.departed;

          const applyVisaOffCascade = () => {
            props.setFieldValue("visaIssued", false, true);
            props.setFieldValue("visaIssuedDate", undefined, true);
            props.setFieldValue("labourIssued", false, true);
            props.setFieldValue("lmisIssuedDate", undefined, true);
            props.setFieldValue("ticketBooked", false, true);
            props.setFieldValue("flight.departed", false, true);
            setStatus(
              computePipelineSubmitStatus(EmployeeStatus.contracted, {
                visaIssued: false,
                labourIssued: false,
                ticketBooked: false,
                flight: { ...v.flight, departed: false },
              }),
            );
          };

          const applyLabourOffCascade = () => {
            props.setFieldValue("labourIssued", false, true);
            props.setFieldValue("lmisIssuedDate", undefined, true);
            props.setFieldValue("ticketBooked", false, true);
            props.setFieldValue("flight.departed", false, true);
            setStatus(
              computePipelineSubmitStatus(status, {
                ...v,
                labourIssued: false,
                ticketBooked: false,
                flight: { ...v.flight, departed: false },
              }),
            );
          };

          const onVisaChange = (checked: boolean) => {
            if (checked && medicalUnfit) return;
            if (
              checked &&
              (!contractedOn || v.medicalStatus !== MedicalStatus.fit)
            )
              return;
            if (checked && !isNoEmbassy && !biometricsOn) return;
            if (!checked && !visaOn) return;
            if (!checked) {
              applyVisaOffCascade();
              return;
            }
            props.setFieldValue("visaIssued", true, true);
            if (!v.visaIssuedDate) {
              props.setFieldValue("visaIssuedDate", new Date(), true);
            }
            setStatus(
              computePipelineSubmitStatus(status, {
                ...v,
                visaIssued: true,
              }),
            );
          };

          const onLabourChange = (checked: boolean) => {
            if (checked && v.medicalStatus !== MedicalStatus.fit) return;
            if (checked && (!contractedOn || !v.cocCompleted)) return;
            if (!checked && !labourOn) return;
            if (!checked) {
              applyLabourOffCascade();
              return;
            }
            props.setFieldValue("labourIssued", true, true);
            if (!v.lmisIssuedDate) {
              props.setFieldValue("lmisIssuedDate", new Date(), true);
            }
            setStatus(
              computePipelineSubmitStatus(status, {
                ...v,
                labourIssued: true,
              }),
            );
          };

          const onTicketChange = (checked: boolean) => {
            if (checked && medicalUnfit) return;
            if (checked && (!visaOn || !labourOn)) return;
            if (!checked && !ticketOn) return;
            props.setFieldValue("ticketBooked", checked, true);
            if (checked && !v.flight?.flightDateTime) {
              props.setFieldValue("flight.flightDateTime", new Date(), true);
            }
            if (!checked) {
              props.setFieldValue("flight.departed", false, true);
              setStatus(EmployeeStatus.labour_issued);
            } else {
              setStatus(EmployeeStatus.flight_booked);
            }
          };

          const onDepartedChange = (checked: boolean) => {
            if (offLocked) return;
            if (checked && medicalUnfit) return;
            if (checked && !ticketOn) return;
            if (!checked && !departedSwitchOn) return;
            if (checked) {
              setStatus(EmployeeStatus.departed);
              props.setFieldValue("flight.departed", true, true);
              props.setFieldValue("currentStatusDate", new Date(), true);
            } else {
              props.setFieldValue("flight.departed", false, true);
              setStatus(EmployeeStatus.flight_booked);
            }
          };

          const step0Done = selectedOn;
          const step1Done = contractedOn;
          const step2Done = visaOn && labourOn;
          const step3Done = ticketOn;
          const step4Done =
            departedSwitchOn ||
            (DISPOSITION_STATUS_OPTIONS.includes(status) &&
              pipelineRank(status) >
                PIPELINE_ORDER.indexOf(EmployeeStatus.departed));

          const activeStep = !step0Done
            ? 0
            : !step1Done
              ? 1
              : !step2Done
                ? 2
                : !step3Done
                  ? 3
                  : 4;

          const switchSx = {
            textAlign: theme.direction === "ltr" ? "left" : "right",
          } as const;

          const medicalOn = v.medicalStatus === MedicalStatus.fit;
          const cocOn = Boolean(v.cocCompleted);
          const offStatus: EmployeeStatus | null =
            SHOW_DISCONTINUED_PROCESS_STATUSES.has(status)
              ? EmployeeStatus.discontinued
              : null;
          const offLocked =
            status === EmployeeStatus.discontinued ||
            status === EmployeeStatus.stopped;
          const showOffProcessSwitch = offStatus !== null;
          const offProcessLabel = "Discontinue Process";
          const canEditOffProcess = canEditDiscontinuedProcess;

          return (
            <Form>
              <Grid container spacing={1} sx={{ px: 1, pt: 1 }}>
                <Grid container item xs={12} spacing={1} sx={{ p: 1 }}>
                  <Grid item xs={12} md={8}>
                    <FormikTextField
                      formikKey="givenName"
                      label={t("candidate") + " " + t("fullName")}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormikTextField
                      formikKey="visa.visaNumber"
                      label={t("visa_number")}
                      disabled
                    />
                  </Grid>
                  {/* <Grid item xs={8}>
                    <FormikTextField
                      formikKey="visa.fullName"
                      label={t("full_name")}
                      disabled
                    />
                  </Grid> */}
                </Grid>

                <Grid container item xs={12} spacing={1}>
                  <Grid item xs={12} md={4} sx={switchSx}>
                    <FormControlLabel
                      style={{ marginBottom: 0 }}
                      disabled={!canEditCocSwitch || offLocked || labourOn}
                      control={
                        <Switch
                          color="secondary"
                          checked={Boolean(props.values.cocCompleted)}
                          name="cocCompleted"
                          onChange={(e) => {
                            const next = e.target.checked;
                            if (!next && labourOn) {
                              applyLabourOffCascade();
                            }
                            props.setFieldValue("cocCompleted", next, true);
                          }}
                        />
                      }
                      label={te("coc")}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} sx={switchSx}>
                    <Stack spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {te("medical")}
                      </Typography>
                      <Box>
                        <MedicalStatusChip status={v.medicalStatus} />
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4} sx={switchSx}>
                    <FormControlLabel
                      style={{ marginBottom: 0 }}
                      disabled={!changeEmployeeStatusOnly || offLocked}
                      control={
                        <Switch
                          color="secondary"
                          checked={Boolean(props.values.biometricsCompleted)}
                          name="biometricsCompleted"
                          onChange={(e) => {
                            props.setFieldValue(
                              "biometricsCompleted",
                              e.target.checked,
                              true,
                            );
                          }}
                        />
                      }
                      label={te("biometrics")}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    {/* <Divider sx={{ my: 1 }} /> */}
                    {/* <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      gutterBottom
                    >
                      {t("processing_steps")}
                    </Typography> */}
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                      }}
                    >
                      <Stepper
                        nonLinear
                        orientation="vertical"
                        sx={{
                          [`& .${stepConnectorClasses.line}`]: {
                            borderColor: (t) => t.palette.secondary.light,
                          },
                          [`& .MuiStepLabel-root`]: {
                            alignItems: "flex-start",
                            py: 0,
                          },
                          [`& .MuiStepContent-root`]: {
                            marginTop: 0,
                            paddingTop: 0,
                            paddingBottom: 0.25,
                          },
                          [`& .MuiCollapse-root`]: { margin: 0 },
                          [`& .MuiFormControlLabel-root`]: {
                            marginTop: 0,
                            marginBottom: 0,
                            marginLeft: 0,
                            alignItems: "center",
                          },
                        }}
                      >
                        <Step
                          completed={step0Done}
                          active={activeStep === 0}
                          expanded
                        >
                          <StepLabel StepIconComponent={PipelineStepIcon}>
                            {te("selected")}
                          </StepLabel>
                          <StepContent>
                            <Chip
                              size="small"
                              label={te(selectedOn ? "selected" : "new")}
                              color={selectedOn ? "info" : "default"}
                              variant="outlined"
                              sx={{ mt: 0.25 }}
                            />
                          </StepContent>
                        </Step>

                        <Step
                          completed={step1Done}
                          active={activeStep === 1}
                          expanded
                        >
                          <StepLabel StepIconComponent={PipelineStepIcon}>
                            {te("contracted")}
                          </StepLabel>
                          <StepContent>
                            <Chip
                              size="small"
                              label={te(
                                contractedOn
                                  ? "contracted"
                                  : selectedOn
                                    ? "selected"
                                    : "new",
                              )}
                              color={contractedOn ? "success" : "default"}
                              variant="outlined"
                              sx={{ mt: 0.25 }}
                            />
                          </StepContent>
                        </Step>

                        <Step
                          completed={step2Done}
                          active={activeStep === 2}
                          expanded
                        >
                          <StepLabel StepIconComponent={PipelineStepIcon}>
                            {te("visa_issued")} / {te("labour_issued")}
                          </StepLabel>
                          <StepContent>
                            <Grid container spacing={0}>
                              <Grid item xs={12} sm={6} sx={switchSx}>
                                <FormControlLabel
                                  sx={{ ml: 0 }}
                                  disabled={
                                    !canVisa ||
                                    ((!contractedOn ||
                                      !medicalOn ||
                                      medicalUnfit ||
                                      (!isNoEmbassy && !biometricsOn)) &&
                                      !visaOn) ||
                                    offLocked
                                  }
                                  control={
                                    <Switch
                                      color="secondary"
                                      checked={visaOn}
                                      onChange={(e) =>
                                        onVisaChange(e.target.checked)
                                      }
                                    />
                                  }
                                  label={te("visa_issued")}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} sx={switchSx}>
                                <FormControlLabel
                                  sx={{ ml: 0 }}
                                  disabled={
                                    !canLabour ||
                                    ((!contractedOn ||
                                      !cocOn ||
                                      !medicalOn) &&
                                      !labourOn) ||
                                    offLocked
                                  }
                                  control={
                                    <Switch
                                      color="secondary"
                                      checked={labourOn}
                                      onChange={(e) =>
                                        onLabourChange(e.target.checked)
                                      }
                                    />
                                  }
                                  label={te("labour_issued")}
                                />
                              </Grid>
                              {visaOn && (
                                <Grid item xs={12} sm={6}>
                                  <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                    adapterLocale={locale}
                                  >
                                    <DatePicker
                                      label={t("visaIssuedDate")}
                                      views={["year", "month", "day"]}
                                      minDate={new Date("2023-01-01")}
                                      disableMaskedInput
                                      inputFormat="DD-MMM-YYYY"
                                      value={
                                        v.visaIssuedDate
                                          ? new Date(
                                              v.visaIssuedDate as Date | string,
                                            )
                                          : null
                                      }
                                      onChange={(value) => {
                                        props.setFieldValue(
                                          "visaIssuedDate",
                                          value,
                                          true,
                                        );
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          fullWidth
                                          size="small"
                                          disabled={!canSave || offLocked}
                                          sx={{ mt: 0.5 }}
                                        />
                                      )}
                                    />
                                  </LocalizationProvider>
                                </Grid>
                              )}
                              {labourOn && (
                                <Grid item xs={12} sm={6}>
                                  <LocalizationProvider
                                    dateAdapter={AdapterDayjs}
                                    adapterLocale={locale}
                                  >
                                    <DatePicker
                                      label={t("lmisIssuedDate")}
                                      views={["year", "month", "day"]}
                                      minDate={new Date("2023-01-01")}
                                      disableMaskedInput
                                      inputFormat="DD-MMM-YYYY"
                                      value={
                                        v.lmisIssuedDate
                                          ? new Date(
                                              v.lmisIssuedDate as Date | string,
                                            )
                                          : null
                                      }
                                      onChange={(value) => {
                                        props.setFieldValue(
                                          "lmisIssuedDate",
                                          value,
                                          true,
                                        );
                                      }}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          fullWidth
                                          size="small"
                                          disabled={!canSave || offLocked}
                                          sx={{ mt: 0.5 }}
                                        />
                                      )}
                                    />
                                  </LocalizationProvider>
                                </Grid>
                              )}
                            </Grid>
                          </StepContent>
                        </Step>

                        <Step
                          completed={step3Done}
                          active={activeStep === 3}
                          expanded
                        >
                          <StepLabel StepIconComponent={PipelineStepIcon}>
                            {te("flight_booked")}
                          </StepLabel>
                          <StepContent>
                            <FormControlLabel
                              sx={{ ml: 0 }}
                              disabled={
                                !canTicket ||
                                (!ticketOn &&
                                  (!visaOn || !labourOn || medicalUnfit)) ||
                                offLocked
                              }
                              control={
                                <Switch
                                  color="secondary"
                                  checked={ticketOn}
                                  onChange={(e) =>
                                    onTicketChange(e.target.checked)
                                  }
                                />
                              }
                              label={te("flight_booked")}
                            />
                            {ticketOn && (
                              <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                adapterLocale={locale}
                              >
                                <DateTimePicker
                                  label={t("flight_date_time")}
                                  views={[
                                    "year",
                                    "month",
                                    "day",
                                    "hours",
                                    "minutes",
                                  ]}
                                  minDate={new Date("2023-01-01")}
                                  disableMaskedInput
                                  inputFormat="DD-MMM-YYYY hh:mm A"
                                  value={
                                    props.values.flight?.flightDateTime
                                      ? new Date(
                                          props.values.flight.flightDateTime as
                                            | Date
                                            | string,
                                        )
                                      : null
                                  }
                                  onChange={(value) => {
                                    props.setFieldValue(
                                      "flight.flightDateTime",
                                      value,
                                      true,
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      id="flightDateTimeStep"
                                      sx={{ mt: 0.5 }}
                                      fullWidth
                                      size="small"
                                      disabled={!canSave || offLocked}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            )}
                          </StepContent>
                        </Step>

                        <Step
                          completed={step4Done}
                          active={activeStep === 4}
                          expanded
                        >
                          <StepLabel StepIconComponent={PipelineStepIcon}>
                            {te("departed")}
                          </StepLabel>
                          <StepContent>
                            <FormControlLabel
                              sx={{ ml: 0 }}
                              disabled={
                                !canDeparted ||
                                (DISPOSITION_STATUS_OPTIONS.includes(status) &&
                                  status !== EmployeeStatus.departed) ||
                                (!ticketOn && !departedSwitchOn) ||
                                (medicalUnfit && !departedSwitchOn) ||
                                offLocked
                              }
                              control={
                                <Switch
                                  color="secondary"
                                  checked={departedSwitchOn}
                                  onChange={(e) =>
                                    onDepartedChange(e.target.checked)
                                  }
                                />
                              }
                              label={te("departed")}
                            />
                          </StepContent>
                        </Step>
                      </Stepper>
                    </Paper>
                  </Grid>

                  {showOffProcessSwitch && selectedEmployee && (
                    <Grid item xs={12}>
                      <Accordion
                        sx={{
                          border: 1,
                          borderColor: "error.light",
                          borderRadius: 1,
                          "&::before": { display: "none" },
                        }}
                      >
                        <StyledAccordionSummary
                          expandIcon={<ExpandMore />}
                          aria-controls="panel-discontinued-content"
                          id="panel-discontinued-header"
                          sx={{
                            bgcolor: (th) => alpha(th.palette.error.main, 0.06),
                            minHeight: 48,
                            "&.Mui-expanded": { minHeight: 48 },
                          }}
                        >
                          <Typography
                            variant="h6"
                            component="div"
                            color="error"
                            fontWeight={700}
                          >
                            {offProcessLabel}
                          </Typography>
                        </StyledAccordionSummary>
                        <AccordionDetails>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Paper
                                variant="outlined"
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  borderWidth: 2,
                                  borderStyle: "dashed",
                                  borderColor: "error.main",
                                  bgcolor: (th) =>
                                    alpha(th.palette.error.main, 0.08),
                                }}
                              >
                                <FormControlLabel
                                  sx={{
                                    ml: 0,
                                    mr: 0,
                                    width: "100%",
                                    justifyContent: "space-between",
                                    gap: 2,
                                  }}
                                  control={
                                    <Switch
                                      checked={offLocked}
                                      color="error"
                                      size="medium"
                                      disabled={!canEditOffProcess}
                                      sx={{
                                        "& .MuiSwitch-track": {
                                          borderRadius: 2,
                                          height: 22,
                                          opacity: 1,
                                        },
                                        "& .MuiSwitch-thumb": {
                                          boxShadow: (th) =>
                                            `0 1px 4px ${alpha(
                                              th.palette.common.black,
                                              0.35,
                                            )}`,
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                          color: "error.contrastText",
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                          {
                                            backgroundColor: "error.main",
                                            opacity: 1,
                                          },
                                      }}
                                      onChange={(e) => {
                                        const on = e.target.checked;
                                          if (on) {
                                          preDiscontinuedStatusRef.current =
                                            status;
                                          setStatus(
                                            offStatus ??
                                              EmployeeStatus.discontinued,
                                          );
                                          props.setFieldValue(
                                            "currentStatusDate",
                                            new Date(),
                                            true,
                                          );
                                        } else {
                                          const prev =
                                            preDiscontinuedStatusRef.current ??
                                            computePipelineSubmitStatus(
                                              EmployeeStatus.contracted,
                                              v,
                                            );
                                          setStatus(prev);
                                          preDiscontinuedStatusRef.current =
                                            null;
                                        }
                                      }}
                                    />
                                  }
                                  labelPlacement="start"
                                  label={
                                    <Box sx={{ pr: 1, maxWidth: "72%" }}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block" }}
                                      >
                                        {t("discontinued_process_hint")}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </Paper>
                            </Grid>
                            <Grid item xs={12}>
                              <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                adapterLocale={locale}
                              >
                                <DatePicker
                                  label={t("discontinued_date")}
                                  views={["year", "month", "day"]}
                                  minDate={new Date("2023-01-01")}
                                  disableMaskedInput
                                  inputFormat="DD-MMM-YYYY"
                                  value={
                                    v.currentStatusDate
                                      ? new Date(
                                          v.currentStatusDate as Date | string,
                                        )
                                      : null
                                  }
                                  onChange={(value) => {
                                    props.setFieldValue(
                                      "currentStatusDate",
                                      value,
                                      true,
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      fullWidth
                                      size="small"
                                      disabled={
                                        !canEditOffProcess || !offLocked
                                      }
                                      sx={{
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: (th) =>
                                            offLocked
                                              ? alpha(
                                                  th.palette.error.main,
                                                  0.45,
                                                )
                                              : undefined,
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  )}

                  {selectedEmployee && accordionOpen && (
                    <Grid item xs={12}>
                      <Accordion expanded>
                        <StyledAccordionSummary
                          expandIcon={<ExpandMore />}
                          aria-controls="panel1a-content"
                          id="panel1a-header"
                        >
                          <Typography variant="h6" component="div">
                            {t("currentStatus")}
                          </Typography>
                        </StyledAccordionSummary>

                        <AccordionDetails>
                          <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Grid item xs={12}>
                              <TextField
                                id="disposition-statuses"
                                select
                                label={t("after_flight_departure_status")}
                                onChange={(e) => {
                                  const newStatus = e.target
                                    .value as EmployeeStatus;
                                  setStatus(newStatus);
                                  if (newStatus === EmployeeStatus.departed) {
                                    props.setFieldValue(
                                      "flight.departed",
                                      true,
                                      true,
                                    );
                                  } else {
                                    props.setFieldValue(
                                      "flight.departed",
                                      false,
                                      true,
                                    );
                                  }
                                  if (newStatus !== dispositionValue) {
                                    props.setFieldValue(
                                      "currentStatusDate",
                                      new Date(),
                                      true,
                                    );
                                  }
                                }}
                                value={dispositionValue}
                                variant="outlined"
                                size="small"
                                fullWidth
                                disabled={!canDisposition}
                                sx={{ mt: 1 }}
                              >
                                {DISPOSITION_STATUS_OPTIONS.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {te(option.toLowerCase())}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            {/* <Grid item xs={12}>
                              <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                adapterLocale={locale}
                              >
                                <DateTimePicker
                                  label={t("flight_date_time")}
                                  views={[
                                    "year",
                                    "month",
                                    "day",
                                    "hours",
                                    "minutes",
                                  ]}
                                  minDate={new Date("2023-01-01")}
                                  disableMaskedInput
                                  inputFormat="DD-MMM-YYYY hh:mm A"
                                  value={
                                    props.values.flight?.flightDateTime
                                      ? new Date(
                                          props.values.flight
                                            .flightDateTime as Date | string,
                                        )
                                      : null
                                  }
                                  onChange={(value) => {
                                    props.setFieldValue(
                                      "flight.flightDateTime",
                                      value,
                                      true,
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      id="flightDateTime"
                                      sx={{ mt: 0 }}
                                      fullWidth
                                      size="small"
                                      disabled={!canSave}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid> */}
                            <Grid item xs={12}>
                              <LocalizationProvider
                                dateAdapter={AdapterDayjs}
                                adapterLocale={locale}
                              >
                                <DatePicker
                                  label={t("currentStatusDate")}
                                  views={["year", "month", "day"]}
                                  minDate={new Date("2023-01-01")}
                                  disableMaskedInput
                                  inputFormat="DD-MMM-YYYY"
                                  value={
                                    props.values.currentStatusDate
                                      ? new Date(
                                          props.values.currentStatusDate as
                                            | Date
                                            | string,
                                        )
                                      : null
                                  }
                                  onChange={(value) => {
                                    props.setFieldValue(
                                      "currentStatusDate",
                                      value,
                                      true,
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      id="currentStatusDate"
                                      sx={{ mt: 1 }}
                                      fullWidth
                                      size="small"
                                      disabled={!canSave}
                                    />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mt: 1,
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 1 }}
                      >
                        {t("status_update_remark_hint")}
                      </Typography>
                      <FormikTextField
                        formikKey="currentStatusRemark"
                        label={t("currentStatusRemark")}
                        multiline
                        minRows={3}
                        disabled={!canSubmitProcessStages}
                        sx={{ mt: 0 }}
                      />
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      color="secondary"
                      variant="contained"
                      size="large"
                      disabled={!canSubmitProcessStages}
                      sx={{ mt: 1, width: "100%" }}
                    >
                      <Save />
                      {t("save") + " " + t("status")}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>

              <br />
              {statusChanged && (
                <Toast severity="success">{statusChanged.message}</Toast>
              )}
            </Form>
          );
        }}
      </Formik>
      {changeFailed && <Toast severity="error">{changeFailed.message}</Toast>}
    </Box>
  );
};
